/**
 * TRAC-862 — the last-resort path to a human.
 *
 * These values are compiled into the bundle ON PURPOSE. Every other route to a
 * person in this product runs through the agent (`route_to_human` is an agent
 * tool) or through the backend's LivePerson bridge. Both are unavailable in the
 * exact situation this exists for: the chat has exhausted its retries because
 * something upstream is dead.
 *
 * So this module must have NO runtime dependencies — no fetch, no config
 * lookup, no feature flag. If rendering these constants requires the network,
 * they are worthless.
 *
 * Source of truth is `agents/tire_agent_v2/agentv2/tools/route_to_human.py`
 * (`_CONTACT`). If the phone tree changes there, change it here — a drift test
 * can't span the language boundary, so this comment is the seam.
 */

export const HUMAN_CONTACT = {
  phone: "1-800-428-8355",
  hours: "Mon-Fri 8AM-8PM ET, Sat 9AM-4PM ET, Sun Closed",
  contactFormUrl: "https://www.tirerack.com/about/contact-form.jsp",
  extensions: {
    sales: "4370",
    customerCare: "4360",
  },
} as const;

/**
 * Overlay copy shown when auto-retry has exhausted (`onExhausted`).
 *
 * Markdown — `AnnouncementBanner` renders it through ReactMarkdown/remark-gfm.
 * Deliberately leads with what the customer can DO. The old behaviour here was
 * a dispatched event that nothing listened for, i.e. a dead chat window and no
 * way forward at all.
 */
const CONTACT_BULLETS = [
  `- **Call ${HUMAN_CONTACT.phone}** — Sales ext. ${HUMAN_CONTACT.extensions.sales}, ` +
    `Customer Care ext. ${HUMAN_CONTACT.extensions.customerCare}`,
  `- ${HUMAN_CONTACT.hours}`,
  `- Or [send us a message](${HUMAN_CONTACT.contactFormUrl})`,
];

/**
 * TRAC-975 — what a customer reads when the agent cannot answer.
 *
 * The backend serves this string (/api/settings/announcement, and the emergency
 * shutoff raises it as an HTTP detail). The two frontend uses are fallbacks for
 * when neither reaches the browser, so the sentence existed in four places
 * written out longhand. Four copies of one sentence drift; three get updated
 * and a customer finds the fourth.
 *
 * Distinct from RETRY_EXHAUSTED_MESSAGE below: that one is the end of the road
 * (we tried, it is not coming back, here is the phone number). This one is
 * "not right now", and is what the auto-maintenance overlay shows.
 *
 * The name matters here. "Tire Expert" is what Tire Rack calls its salespeople
 * (bridge.py sets it as the LivePerson rep display name), so the previous
 * wording said the tire expert was unavailable directly above a button
 * offering to talk to one. "AI Assist" is what the chat header already says,
 * so it names the thing that is down in vocabulary the customer is looking at.
 * Ruled by Dylan 2026-09-08.
 *
 * Backend twin: backend/src/constants.py AGENT_UNAVAILABLE_MESSAGE. A drift
 * test cannot span the language boundary, so this comment is the seam — the
 * same arrangement this file already uses for the phone tree above.
 */
export const AGENT_UNAVAILABLE_MESSAGE =
  "AI Assist is temporarily unavailable. Please try again in a few minutes.";

export const RETRY_EXHAUSTED_MESSAGE = [
  "**We can't reach our tire expert right now.**",
  "",
  "Sorry about that — we tried a few times and it isn't coming back.",
  "A person can pick up where this left off:",
  "",
  ...CONTACT_BULLETS,
].join("\n");

/**
 * TRAC-975 — appended to ANY emergency overlay when live chat is closed.
 *
 * The overlay's whole job is telling the customer what is still available.
 * When LivePerson is open that is a button (see `overlayHumanRoute`); when it
 * is shut there is still a person on the end of a phone, and the
 * auto-maintenance copy alone mentions neither. Same constants, same no-network
 * guarantee as `RETRY_EXHAUSTED_MESSAGE` — this is the honest closed state, not
 * a disabled button that implies live chat might come back if you wait.
 */
export const LIVE_CHAT_CLOSED_CONTACT = [
  "Live chat is closed right now, but a person can still help:",
  "",
  ...CONTACT_BULLETS,
].join("\n");

/**
 * TRAC-1006 — what the customer reads when they asked for a person and nobody
 * can take the chat (outside live-chat hours, or every rep busy).
 *
 * This used to be written by the agent: the page sent it a hidden
 * "[SYSTEM EVENT — LIVE CHAT UNAVAILABLE]" turn and let it decline in its own
 * voice. During an outage the agent is exactly what is down, so a customer who
 * asked for a person after hours was told "Something went wrong" instead of when
 * we reopen. It is now composed here, from the decline response and the same
 * compiled-in phone tree as every other last-resort path — no network needed.
 *
 * `hours` is LivePerson's next-open note ("tomorrow at 8 AM ET"), set only for
 * `closed_hours` (backend api.py escalate-to-human). Any other reason is treated
 * as a staffed desk with nobody free, the same split the agent note used.
 *
 * TRACI-1055: `provider` "connect" with `testRoute` (off prod, a tester's chat sent to the
 * Connect desk with no LivePerson fallback) names the desk instead, so a tester can tell its
 * own hours or an empty desk from a busy team; `hours` is then the desk's opening ("tomorrow
 * at 8 AM ET"). CPOC-109: labelled "Tester note", and never without `testRoute`, so a real
 * shopper always reads the LivePerson copy.
 */
export function handoffDeclinedMessage(reason: string | null, hours: string | null,
                                       provider: string | null = null,
                                       testRoute = false): string {
  const closed = reason === "closed_hours";
  const lead = provider === "connect" && testRoute
    ? closed
      ? `**Tester note: the Connect desk is closed right now.**${hours ? ` It opens ${hours}.` : ""}`
      : "**Tester note: no rep is Available on the Connect desk right now.**"
    : closed
    ? `**Live chat with a Tire Expert is closed right now.**${hours ? ` We're back ${hours}.` : ""}`
    : "**Every Tire Expert is helping another customer right now.**";
  return [lead, "", "You can still reach a person:", "", ...CONTACT_BULLETS].join("\n");
}

/**
 * TRAC-1007 — what a customer reads when they have waited a minute for a rep and
 * nobody has picked up.
 *
 * Reps join in a median of 8 seconds, but of the customers who gave up waiting, 70%
 * left inside that first minute, looking at "Connecting you…" and nothing else. A
 * silent wait reads as broken. This says we are still trying, and gives the number
 * for the team they asked for, so leaving the chat does not mean leaving Tire Rack.
 *
 * The customer stays in the queue (Dylan, 15 Sep), so nothing here may read as the
 * attempt having failed. `department` is whatever the backend confirmed, or the team
 * they picked; anything else is treated as unknown and both extensions are given.
 * Same compiled-in phone tree as every other last-resort path — no network needed.
 */
function teamCall(department: string | null | undefined): [string, string] {
  const d = (department || "").trim().toLowerCase();
  const { phone, extensions } = HUMAN_CONTACT;
  return d === "sales"
    ? ["our Sales team", `**${phone}, ext. ${extensions.sales}**`]
    : d === "customer care"
      ? ["our Customer Care team", `**${phone}, ext. ${extensions.customerCare}**`]
      : [
          "a Tire Expert",
          `**${phone}** (Sales ext. ${extensions.sales}, Customer Care ext. ${extensions.customerCare})`,
        ];
}

export function handoffSlowMessage(department: string | null | undefined): string {
  const [team, call] = teamCall(department);
  return [
    `**We're still trying to reach ${team}.**`,
    "",
    "It's taking longer than usual, but you're still in line. Stay here and you'll be connected as soon as someone is free.",
    "",
    `Prefer to call? ${call}`,
  ].join("\n");
}

/**
 * CPOC-150: the backend ended a live chat nobody joined (reason "unjoined") and no other
 * team could take it. Same phone line as the still-trying notice.
 */
export function handoffUnjoinedMessage(department: string | null | undefined): string {
  // System lines render as plain text, so the bold markers the notice card uses are dropped.
  const call = teamCall(department)[1].replace(/\*\*/g, "");
  return `Nobody could pick up the live chat just now, so the AI tire agent is back with you. You can try again, or call us at ${call}.`;
}

/**
 * TRAC-1035 — the same message as it is drawn on screen: one compact line under the
 * "Connecting you to our … team now…" heading. As a block inside the waiting card it
 * pushed the "Did you know?" tip down at the moment the customer was already impatient.
 * The saved message keeps the full wording above; only its on-screen form is this.
 *
 * Kept short enough to sit on one line in the ~400 px embed for either team, and in two
 * parts so the number can be set apart without changing the line's width.
 */
export function handoffSlowStatus(department: string | null | undefined): { lead: string; call: string } {
  const d = (department || "").trim().toLowerCase();
  const { phone, extensions } = HUMAN_CONTACT;
  if (d === "sales") return { lead: "Still trying to reach Sales", call: `${phone}, ext. ${extensions.sales}` };
  if (d === "customer care") {
    return { lead: "Still trying to reach Customer Care", call: `${phone}, ext. ${extensions.customerCare}` };
  }
  return {
    lead: "Still trying to reach a Tire Expert",
    call: `${phone} (Sales ext. ${extensions.sales}, Customer Care ext. ${extensions.customerCare})`,
  };
}

/**
 * TRAC-1035 — which team a saved still-trying message was written for, so the line drawn
 * from it after a reload or a page change names the same team and number. Read by
 * comparing against the wording this module writes rather than by picking the text apart;
 * anything it did not write (the both-numbers message, or wording from before a copy
 * change) reads as unknown, which still draws a line with both numbers.
 */
export function handoffSlowDepartment(text: string): "Sales" | "Customer Care" | null {
  for (const team of ["Sales", "Customer Care"] as const) {
    if (text === handoffSlowMessage(team)) return team;
  }
  return null;
}
